var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { SystemPerformanceNodeType, } from "api/types";
import { liftingForcesBoxIdPrefix, liftingForcesYAxisHorizontalKey, liftingForcesYAxisVerticalKey, } from "ui/components/panels/graphs/constants";
export var getGoverningPump = function (nodeData, governingPumpId) {
    var e_1, _a;
    var governingPump = { id: "", node: {} };
    try {
        for (var _b = __values(Object.entries(nodeData)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), nodeId = _d[0], node = _d[1];
            if (!node.vacuumPump)
                continue;
            // We give the pump a value before checking if it's the governing pump, if the API
            // has an incorrect value for governing pump ID, we atleast need to return something.
            governingPump = {
                id: nodeId,
                node: node,
            };
            var isGoverningPump = node.vacuumPump.id === governingPumpId;
            if (isGoverningPump) {
                break;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return governingPump;
};
var getNodesByType = function (nodeData, nodeType) {
    var nodes = Object.entries(nodeData)
        .filter(function (_a) {
        var _b = __read(_a, 2), _nodeId = _b[0], node = _b[1];
        return node[nodeType];
    })
        .map(function (_a) {
        var _b = __read(_a, 2), nodeId = _b[0], node = _b[1];
        return {
            id: nodeId,
            performanceData: node[nodeType],
        };
    });
    return nodes;
};
export var getVacuumPumps = function (nodeData) {
    return getNodesByType(nodeData, SystemPerformanceNodeType.VACUUM_PUMP);
};
export var getSuctionCups = function (nodeData) {
    return getNodesByType(nodeData, SystemPerformanceNodeType.SUCTION_CUP);
};
/**
 * Index into `timeData` where the suction cups have returned to atmospheric
 * after the part is released.
 *
 * The API's `place_stop_index` (exposed as `placeStopIndex`) is
 * `place_start_index` advanced by `blow_off_time_to_atm` — a nominal blow-off
 * duration. At that
 * instant the pump is already blowing overpressure, while the cups, being
 * downstream through the tubing, still hold several kPa of vacuum (and lifting
 * force). Marking ATM there is visibly too early. Instead we find the first
 * sample at or after the blow-off start (`placeStartIndex`) where *every* cup
 * has dropped to within `tolerance` of atmospheric — i.e. when the part has
 * actually released.
 *
 * Returns `fallbackIndex` when it can't be determined (no cups, no place-start,
 * or the cups never return to atmospheric).
 *
 * Note: cup `vacuumLevels` here are the raw API values (positive = vacuum), not
 * the negated values used by the Recharts data.
 */
export var findCupReleaseIndex = function (suctionCups, placeStartIndex, fallbackIndex, tolerance) {
    var _a, _b;
    if (!suctionCups.length || placeStartIndex <= 0)
        return fallbackIndex;
    var cupLevels = suctionCups.map(function (cup) { return cup.performanceData.vacuumLevels; });
    var sampleCount = (_b = (_a = cupLevels[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    var _loop_1 = function (i) {
        var allReleased = cupLevels.every(function (levels) { var _a; return ((_a = levels[i]) !== null && _a !== void 0 ? _a : 0) <= tolerance; });
        if (allReleased)
            return { value: i };
    };
    for (var i = placeStartIndex; i < sampleCount; i++) {
        var state_1 = _loop_1(i);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    return fallbackIndex;
};
/**
 * Fraction of a cup's highest vacuum level (before release) at which its curve
 * counts as levelled out.
 */
export var CUP_SETTLED_LEVEL_FRACTION = 0.9;
var latestTime = function (times) {
    var positiveTimes = times.filter(function (time) { return typeof time === "number" && time > 0; });
    return positiveTimes.length ? Math.max.apply(Math, __spreadArray([], __read(positiveTimes), false)) : undefined;
};
/**
 * API times (seconds) by which every suction cup has reached part secured
 * (when it does) and by which every cup has levelled out, i.e. reached
 * {@link CUP_SETTLED_LEVEL_FRACTION} of its highest level before release
 * (`placeStartIndex`). The later of the two is when all cup lines in the
 * pressure graph have flattened out. The system PS time (taken from the pumps)
 * is typically well before it: the cups pass the PS level while still building
 * up vacuum, and they lag behind the pumps through the tubing.
 *
 * Each is undefined when no cup gives such a time.
 *
 * Note: cup `vacuumLevels` here are the raw API values (positive = vacuum).
 */
export var findCupsSettledTimes = function (suctionCups, timeData, placeStartIndex) {
    var endIndex = placeStartIndex > 0 ? placeStartIndex : timeData.length;
    var levelledOutTimes = suctionCups.map(function (_a) {
        var performanceData = _a.performanceData;
        var levels = performanceData.vacuumLevels.slice(0, endIndex);
        var maxLevel = levels.reduce(function (max, level) { return Math.max(max, level); }, 0);
        return maxLevel > 0
            ? timeData[levels.findIndex(function (level) { return level >= maxLevel * CUP_SETTLED_LEVEL_FRACTION; })]
            : undefined;
    });
    return {
        partSecured: latestTime(suctionCups.map(function (_a) {
            var performanceData = _a.performanceData;
            return performanceData.timeToPartSecured;
        })),
        levelledOut: latestTime(levelledOutTimes),
    };
};
export var isVerticalForce = function (dataKey) {
    return dataKey.startsWith(liftingForcesYAxisVerticalKey);
};
export var boxLiftingForceDataKey = function (forceKey, boxId) { return "".concat(forceKey, "-").concat(liftingForcesBoxIdPrefix).concat(boxId); };
/* Whether a lifting forces data key is a box total rather than a cup. */
export var isBoxLiftingForce = function (dataKey) {
    return dataKey.startsWith(boxLiftingForceDataKey(liftingForcesYAxisVerticalKey, "")) ||
        dataKey.startsWith(boxLiftingForceDataKey(liftingForcesYAxisHorizontalKey, ""));
};
export var filterDataToDomain = function (timeDomain, data) {
    var min = timeDomain[0];
    var max = timeDomain[1];
    var firstInDomain = data.findIndex(function (point) { return point.time >= min; });
    var firstOverDomain = data.findIndex(function (point) { return point.time > max; });
    return data.slice(Math.max(0, firstInDomain - 1), firstOverDomain === -1 ? data.length : firstOverDomain + 1);
};
export var canFitTickForZero = function (chartDomain, ticks) {
    var min = chartDomain.min, max = chartDomain.max;
    var range = max - min;
    return (min < 0 && max > 0 && ticks.every(function (tick) { return Math.abs(tick) > range * 0.12; })); // if all ticks are more than 12% of the chart range away from 0, we can fit zero as a tick without having the ticks overlap
};
